import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import { MenuItem, styled } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Chevron from '../../assets/chevron-left.svg';

const ALL_OPTION_VALUE = 'all';

export type MultiSelectProps = {
    value: string[];
    onChange: (values: string[]) => void;
    options: string[];
    placeholder?: string;
} & Omit<SelectProps, 'onChange'>;

export const MultiSelect: React.FunctionComponent<MultiSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    ...selectProps
}) => {
    const { t } = useTranslation('Common');
    const [isOpen, setIsOpen] = useState(false);

    const isAllSelected = value.length === 0;

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const rawValue = event.target.value;
        const selected = typeof rawValue === 'string' ? rawValue.split(',') : (rawValue as string[]);

        // If 'all' is in the selection, clear everything
        onChange(selected.includes(ALL_OPTION_VALUE) ? [] : selected);
    };

    return (
        <StyledSelect
            {...selectProps}
            multiple
            value={value}
            onChange={handleChange}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            displayEmpty
            renderValue={() => (value.length > 0 ? value.join(', ') : (placeholder ?? ''))}
            IconComponent={() => <StyledChevron isOpen={isOpen} />}
            MenuProps={{ PaperProps: { style: { maxHeight: '30rem', overflowY: 'auto' } } }}
        >
            <StyledMenuItem key={ALL_OPTION_VALUE} value={ALL_OPTION_VALUE}>
                <OptionCheckbox isSelected={isAllSelected} />
                {t('All')}
            </StyledMenuItem>
            {options.map((option) => (
                <StyledMenuItem key={option} value={option}>
                    <OptionCheckbox isSelected={value.includes(option)} />
                    {option}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
};

const OptionCheckbox: React.FunctionComponent<{ isSelected: boolean }> = ({ isSelected }) =>
    isSelected ? (
        <CheckBoxIcon fontSize="medium" style={{ marginRight: 8, boxSizing: 'content-box' }} />
    ) : (
        <CheckBoxOutlineBlankIcon fontSize="medium" style={{ marginRight: 8, boxSizing: 'content-box' }} />
    );

// styled() so MUI's default styles don't override ours
const StyledMenuItem = styled(MenuItem)(() => ({
    fontSize: '1.6rem',
}));

const StyledSelect = styled(Select)(() => ({
    width: '100%',
    maxWidth: '24rem',
    height: '4.5rem',
    margin: '0.5rem 0 1rem 2rem',
    borderRadius: '3.7rem',
    fontSize: '1.6rem',
    '& .MuiList-root': {
        fontSize: '1.6rem',
    },
}));

const StyledChevron = styled(Chevron)<{ isOpen?: boolean }>(({ isOpen }) => ({
    transform: isOpen ? 'rotate(90deg)' : 'rotate(-90deg)',
    marginRight: '2rem',
    height: '1.4rem',
    width: '0.9rem',
}));
